import React, { useEffect, useState } from "react";

import { Report } from "@/types";
import { tooltipTitles } from "@/constants/tooltipTitles";

import AppTable from "@/components/AppTable";
import AppTableContainer from "@/components/AppTableContainer";
import AppTableHead from "@/components/AppTable/AppTableHead";
import AppTableRow from "@/components/AppTable/AppTableRow";
import AppTableBody from "@/components/AppTable/AppTableBody";
import AppTableCell from "@/components/AppTable/AppTableCell";
import AppTableHeaderCell from "@/components/AppTable/AppTableHeaderCell";
import AppBox from "@/components/AppBox";
import AppIcon from "@/components/AppIcon";
import AppText from "@/components/AppText";
import AppTooltip from "@/components/AppTooltip";
import AppLoader from "@/components/AppLoader";

interface UserReportsTableProps {
  tableRows: Report[];
  onDownload: (selectedId: number) => void;
  loadingReports: { [key: number]: boolean };
}

const UserReportsTable: React.FC<UserReportsTableProps> = ({
  tableRows,
  onDownload,
  loadingReports,
}) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    // Reset selected IDs when table rows change
    setSelectedIds([]);
  }, [tableRows]);

  const calculateResponseRate = (responded: number, total: number): string => {
    if (total === 0) return '0 (0%)';
    const percentage = (responded / total) * 100;
    return `${responded} (${percentage.toFixed(1)}%)`;
  };

  return (
    <AppTableContainer>
      <AppTable>
        <AppTableHead>
          <AppTableRow>
            <AppTableHeaderCell>Week</AppTableHeaderCell>
            
            <AppTableHeaderCell>
              <AppTooltip title={tooltipTitles.total_leads}>
                <AppBox className="flex items-center gap-1">
                  <AppText>Total Leads</AppText>
                  <AppIcon name="help" size="sm" className="text-gray-400" />
                </AppBox>
              </AppTooltip>
            </AppTableHeaderCell>

            <AppTableHeaderCell>
              <AppTooltip title={tooltipTitles.message_leads}>
                <AppBox className="flex items-center gap-1">
                  <AppText>Message Leads</AppText>
                  <AppIcon name="help" size="sm" className="text-gray-400" />
                </AppBox>
              </AppTooltip>
            </AppTableHeaderCell>

            <AppTableHeaderCell>
              <AppTooltip title={tooltipTitles.message_leads_responded}>
                <AppBox className="flex items-center gap-1">
                  <AppText>Message Leads Responded</AppText>
                  <AppIcon name="help" size="sm" className="text-gray-400" />
                </AppBox>
              </AppTooltip>
            </AppTableHeaderCell>

            <AppTableHeaderCell>
              <AppTooltip title={tooltipTitles.missed_call_leads}>
                <AppBox className="flex items-center gap-1">
                  <AppText>Missed Call Leads</AppText>
                  <AppIcon name="help" size="sm" className="text-gray-400" />
                </AppBox>
              </AppTooltip>
            </AppTableHeaderCell>

            <AppTableHeaderCell>
              <AppTooltip title={tooltipTitles.answered_call_leads}>
                <AppBox className="flex items-center gap-1">
                  <AppText>Answered Call Leads</AppText>
                  <AppIcon name="help" size="sm" className="text-gray-400" />
                </AppBox>
              </AppTooltip>
            </AppTableHeaderCell>

            <AppTableHeaderCell className="text-center">Download</AppTableHeaderCell>
          </AppTableRow>
        </AppTableHead>

        <AppTableBody>
          {tableRows.length ? (
            tableRows.map((row) => (
              <AppTableRow
                key={row.id}
                className={`hover:bg-gray-100 cursor-pointer ${
                  selectedIds.includes(row.id) ? "bg-gray-100" : ""
                }`}
              >

                {/* Week */}
                <AppTableCell>
                  <AppText>{row.week}</AppText>
                </AppTableCell>

                {/* Total */}
                <AppTableCell>
                  <AppText>{row.total}</AppText>
                </AppTableCell>

                {/* Message Leads */}
                <AppTableCell>
                  <AppText>{row.message}</AppText>
                </AppTableCell>

                {/* Message Leads Responded */}
                <AppTableCell>
                  <AppText>
                    {calculateResponseRate(row.message_responded, row.message)}
                  </AppText>
                </AppTableCell>

                {/* Missed Call Leads */}
                <AppTableCell>
                  <AppText>{row.missed_call}</AppText>
                </AppTableCell>

                {/* Answered Call Leads */}
                <AppTableCell>
                  <AppText>{row.answered_call}</AppText>
                </AppTableCell>

                {/* Download */}
                <AppTableCell>
                  <AppBox center>
                    {loadingReports[row.id] ? (
                      <AppLoader size={20} color="primary" />
                    ) : (
                      <AppTooltip title={tooltipTitles.download_user_report}>
                        <AppIcon
                          name="download"
                          className="text-primary cursor-pointer hover:text-primary-hover"
                          onClick={() => onDownload(row.id)}
                        />
                      </AppTooltip>
                    )}
                  </AppBox>
                </AppTableCell>
              </AppTableRow>
            ))
          ) : (
            <AppTableRow>
              <AppTableCell colSpan={7} className="text-center">
                No results
              </AppTableCell>
            </AppTableRow>
          )}
        </AppTableBody>
      </AppTable>
    </AppTableContainer>
  );
};

export default UserReportsTable;